import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { API } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import {
  Search,
  Plus,
  X,
  Key,
  Trash2,
  Shield,
  UserCheck,
  UserX,
  AlertTriangle,
  Loader2,
  Users,
  Mail,
  Phone,
  Building2,
  Clock,
  LogIn
} from 'lucide-react';

const ROLES = ['admin','data_entry','field_officer','viewer'];
const ROLE_COLORS = { super_admin:'badge-red', admin:'badge-purple', data_entry:'badge-blue', field_officer:'badge-green', viewer:'badge-gray' };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPass, setNewPass] = useState('');
  const [form, setForm] = useState({ full_name:'', email:'', password:'', role:'data_entry', department:'', phone:'' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const fetch = () => {
    setLoading(true);
    API.get('/users').then(r => setUsers(r.data)).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.post('/auth/register', form);
      toast.success('User created');
      setShowForm(false);
      setForm({ full_name:'', email:'', password:'', role:'data_entry', department:'', phone:'' });
      fetch();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (id, name, isActive) => {
    try {
      await API.patch(`/users/${id}/toggle`);
      toast.success(`${name} ${isActive ? 'disabled' : 'enabled'}`);
      fetch();
    } catch { toast.error('Failed'); }
  };

  const changeRole = async (id, role) => {
    try {
      await API.patch(`/users/${id}/role`, { role });
      toast.success('Role updated');
      fetch();
    } catch { toast.error('Failed'); }
  };

  const handleResetPassword = async () => {
    if (!newPass || newPass.length < 8) { toast.error('Min 8 characters'); return; }
    try {
      await API.post(`/users/${resetTarget.id}/reset-password`, { new_password: newPass });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null); setNewPass('');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/users/${id}`);
      toast.success('User deleted');
      fetch();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const filtered = users.filter(u =>
    u.full_name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.role.toLowerCase().includes(search.toLowerCase())
  );

  const ROLE_DESC = {
    super_admin: 'Full access — can delete data, manage all users, change settings',
    admin: 'Can create/edit CSOs, certify, publish, manage non-admin users',
    data_entry: 'Can create and edit CSO profiles; cannot publish or certify',
    field_officer: 'Can certify CSOs after physical verification visits',
    viewer: 'Read-only access to admin panel',
  };

  return (
    <div className="anim-fadeIn">
      {/* Role legend */}
      <div className="card card-pad" style={{ marginBottom:20 }}>
        <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:12, display:'flex', alignItems:'center', gap:6 }}>
          <Shield size={18} /> Role Permissions
        </h3>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10 }}>
          {Object.entries(ROLE_DESC).map(([role, desc]) => (
            <div key={role} style={{ padding:'10px 12px', background:'var(--gray-50)', borderRadius:8, border:'1px solid var(--gray-200)' }}>
              <span className={`badge ${ROLE_COLORS[role]}`} style={{ marginBottom:6, display:'inline-flex' }}>{role.replace('_',' ')}</span>
              <p style={{ fontSize:'0.73rem', color:'var(--gray-500)', lineHeight:1.4 }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div className="input-group" style={{ maxWidth:280 }}>
          <span className="input-icon"><Search size={16} /></span>
          <input className="form-control" placeholder="Search users..." value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
          {showForm ? <><X size={16} /> Cancel</> : <><Plus size={16} /> Add New User</>}
        </button>
      </div>

      {/* Create user form */}
      {showForm && (
        <div className="card card-pad" style={{ marginBottom:20 }}>
          <h3 style={{ fontSize:'0.95rem', fontWeight:700, marginBottom:18, display:'flex', alignItems:'center', gap:6 }}>
            <Users size={18} /> Create Staff Account
          </h3>
          <form onSubmit={handleCreate}>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Full Name <span className="req">*</span></label>
                <input className="form-control" required value={form.full_name} onChange={e=>setForm({...form,full_name:e.target.value})}/>
              </div>
              <div className="form-group">
                <label className="form-label">Email Address <span className="req">*</span></label>
                <input type="email" className="form-control" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
              </div>
              <div className="form-group">
                <label className="form-label">Temporary Password <span className="req">*</span></label>
                <input type="password" className="form-control" required minLength={8} value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Min 8 characters"/>
              </div>
              <div className="form-group">
                <label className="form-label">Role <span className="req">*</span></label>
                <select className="form-control" value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
                  {ROLES.map(r=><option key={r} value={r}>{r.replace('_',' ')}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <input className="form-control" value={form.department} onChange={e=>setForm({...form,department:e.target.value})} placeholder="e.g. Data Management"/>
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-control" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/>
              </div>
            </div>
            <div style={{ padding:'12px 16px', background:'var(--gold-50)', borderRadius:8, marginBottom:14, fontSize:'0.8rem', color:'#92400e', display:'flex', alignItems:'flex-start', gap:8 }}>
              <AlertTriangle size={16} style={{ flexShrink:0, marginTop:1 }} />
              <span>The user will be asked to change their password on first login. Share credentials securely.</span>
            </div>
            <button type="submit" disabled={saving} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
              {saving ? <><Loader2 size={16} className="spin" /> Creating...</> : 'Create User Account'}
            </button>
          </form>
        </div>
      )}

      {/* Users table */}
      {loading ? <div className="loading-center"><div className="spin"/></div> : (
        <div className="card">
          <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--gray-200)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:'0.85rem', fontWeight:600 }}>{filtered.length} users</span>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Logins</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:34, height:34, borderRadius:'50%', background:'linear-gradient(135deg,var(--green),var(--green-dark))', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:700, fontSize:'0.75rem', flexShrink:0 }}>
                          {u.full_name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                        </div>
                        <span style={{ fontWeight:600, fontSize:'0.85rem' }}>{u.full_name}</span>
                        {u.id === me?.id && <span className="badge badge-green" style={{ fontSize:'0.65rem' }}>You</span>}
                      </div>
                    </td>
                    <td style={{ fontSize:'0.82rem', color:'var(--gray-600)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <Mail size={12} /> {u.email}
                      </div>
                    </td>
                    <td>
                      {u.role === 'super_admin' ? (
                        <span className={`badge ${ROLE_COLORS.super_admin}`}>{u.role.replace('_',' ')}</span>
                      ) : u.id !== me?.id ? (
                        <select className="form-control" style={{ padding:'4px 8px', fontSize:'0.78rem', width:'auto' }}
                          value={u.role} onChange={e => changeRole(u.id, e.target.value)}>
                          {ROLES.map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
                        </select>
                      ) : (
                        <span className={`badge ${ROLE_COLORS[u.role]}`}>{u.role.replace('_',' ')}</span>
                      )}
                    </td>
                    <td style={{ fontSize:'0.82rem', color:'var(--gray-500)' }}>
                      {u.department ? (
                        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                          <Building2 size={12} /> {u.department}
                        </div>
                      ) : '—'}
                    </td>
                    <td>
                      <span className={`badge ${u.is_active ? 'badge-green' : 'badge-red'}`} style={{ display:'flex', alignItems:'center', gap:4, width:'fit-content' }}>
                        {u.is_active ? <><UserCheck size={12} /> Active</> : <><UserX size={12} /> Disabled</>}
                      </span>
                    </td>
                    <td style={{ fontSize:'0.78rem', color:'var(--gray-400)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <Clock size={12} />
                        {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                      </div>
                    </td>
                    <td style={{ fontSize:'0.82rem', color:'var(--gray-500)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <LogIn size={12} /> {u.login_count || 0}
                      </div>
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:5 }}>
                        {u.role !== 'super_admin' && u.id !== me?.id && (
                          <>
                            <button onClick={() => toggleActive(u.id, u.full_name, u.is_active)}
                              className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-secondary'}`} style={{ padding:'4px 10px', fontSize:'0.75rem' }}>
                              {u.is_active ? 'Disable' : 'Enable'}
                            </button>
                            <button onClick={() => setResetTarget({ id:u.id, name:u.full_name })}
                              className="btn btn-sm btn-secondary" style={{ padding:'4px 10px', fontSize:'0.75rem', display:'flex', alignItems:'center', gap:4 }}>
                              <Key size={12} /> Reset
                            </button>
                            {me?.role === 'super_admin' && (
                              <button onClick={() => handleDelete(u.id, u.full_name)}
                                className="btn btn-sm btn-danger" style={{ padding:'4px 10px', fontSize:'0.75rem' }}>
                                <Trash2 size={14} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <div className="modal-backdrop" onClick={() => setResetTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ fontSize:'1rem', fontWeight:700, display:'flex', alignItems:'center', gap:6 }}>
                <Key size={18} /> Reset Password — {resetTarget.name}
              </h3>
              <button className="modal-close" onClick={() => setResetTarget(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input type="password" className="form-control" value={newPass}
                  onChange={e=>setNewPass(e.target.value)} placeholder="Minimum 8 characters" autoFocus/>
              </div>
              <div className="alert alert-warning" style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                <AlertTriangle size={16} style={{ flexShrink:0, marginTop:1 }} />
                <span>Share the new password securely with the user.</span>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setResetTarget(null)} className="btn btn-ghost">Cancel</button>
              <button onClick={handleResetPassword} className="btn btn-primary">Reset Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}